import { useState } from 'react';
import { X, Plus, Minus, Clock, Flame, AlertTriangle, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { useCart } from '../../context/CartContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const tagLabels = {
  vegetarian: { label: 'Vegetariano', color: 'bg-green-100 text-green-800' },
  vegan: { label: 'Vegano', color: 'bg-green-100 text-green-800' },
  'gluten-free': { label: 'Sin Gluten', color: 'bg-amber-100 text-amber-800' },
  spicy: { label: 'Picante', color: 'bg-red-100 text-red-800' },
};

export function ProductDetail({ product, isOpen, onClose }) {
  const { addItem, openCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [note, setNote] = useState('');
  const [spiceLevel, setSpiceLevel] = useState(product?.spiceLevel || 1);

  if (!product) return null;

  // Calculate total price
  let itemPrice = product.price;
  Object.values(selectedVariants).forEach(v => { itemPrice += v.price || 0; });
  selectedExtras.forEach(e => { itemPrice += e.price || 0; });
  const totalPrice = itemPrice * quantity;

  const handleVariantChange = (variantId, option) => {
    setSelectedVariants(prev => ({
      ...prev,
      [variantId]: option,
    }));
  };

  const handleExtraToggle = (extra) => {
    setSelectedExtras(prev => {
      const exists = prev.find(e => e.id === extra.id);
      if (exists) {
        return prev.filter(e => e.id !== extra.id);
      }
      return [...prev, extra];
    });
  };

  const handleAddToCart = () => {
    // Validate required variants
    const missingVariants = product.variants?.filter(
      v => !selectedVariants[v.id]
    );
    
    if (missingVariants && missingVariants.length > 0) {
      toast.error(`Selecciona: ${missingVariants.map(v => v.name).join(', ')}`);
      return;
    }

    addItem({
      ...product,
      quantity,
      selectedVariants,
      selectedExtras,
      note,
      spiceLevel: product.spiceLevel ? spiceLevel : undefined,
    });

    toast.success(`${product.name} agregado`, {
      description: `${quantity} x $${itemPrice} = $${totalPrice}`,
      action: {
        label: 'Ver carrito',
        onClick: () => openCart(),
      },
    });

    // Reset and close
    setQuantity(1);
    setSelectedVariants({});
    setSelectedExtras([]);
    setNote('');
    onClose();
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent 
        side="right" 
        className="w-full sm:max-w-lg p-0 overflow-y-auto"
        data-testid="product-detail-sheet"
      >
        {/* Image */}
        <div className="relative aspect-video">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover"
          />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-background/80 backdrop-blur-sm flex items-center justify-center hover:bg-background transition-colors"
            data-testid="close-product-detail"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Header */}
          <div>
            <div className="flex flex-wrap gap-2 mb-3">
              {product.tags?.map(tag => (
                <Badge key={tag} variant="secondary" className={tagLabels[tag]?.color}>
                  {tagLabels[tag]?.label}
                </Badge>
              ))}
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium">{product.name}</h2>
            <p className="text-muted-foreground mt-2 leading-relaxed">{product.description}</p>
            
            {/* Meta */}
            <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {product.prepTime} min
              </span>
              {product.calories && (
                <span>{product.calories} kcal</span>
              )}
            </div>
          </div>

          {/* Allergens warning */}
          {product.allergens && product.allergens.length > 0 && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-900 text-sm">Alérgenos</p>
                  <p className="text-amber-700 text-sm mt-0.5">
                    {product.allergens.join(', ')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Spice level selector */}
          {product.spiceLevel && (
            <div>
              <Label className="text-sm font-medium">Nivel de picante</Label>
              <div className="flex gap-2 mt-2">
                {[1, 2, 3].map(level => (
                  <button
                    key={level}
                    onClick={() => setSpiceLevel(level)}
                    className={`flex-1 py-3 rounded-lg border transition-all ${
                      spiceLevel === level
                        ? 'border-accent bg-accent/10'
                        : 'border-border hover:border-accent/50'
                    }`}
                    data-testid={`spice-level-${level}`}
                  >
                    <div className="flex items-center justify-center gap-1">
                      {Array(level).fill(0).map((_, i) => (
                        <Flame key={i} className="w-4 h-4 text-orange-500" />
                      ))}
                    </div>
                    <span className="text-xs text-muted-foreground mt-1 block">
                      {level === 1 ? 'Suave' : level === 2 ? 'Medio' : 'Intenso'}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Variants */}
          {product.variants?.map(variant => (
            <div key={variant.id}>
              <Label className="text-sm font-medium">{variant.name} *</Label>
              <RadioGroup
                value={selectedVariants[variant.id]?.name || ''}
                onValueChange={(value) => {
                  const option = variant.options.find(o => o.name === value);
                  handleVariantChange(variant.id, option);
                }}
                className="mt-2 grid gap-2"
              >
                {variant.options.map(option => (
                  <label
                    key={option.name}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedVariants[variant.id]?.name === option.name
                        ? 'border-accent bg-accent/5'
                        : 'border-border hover:border-accent/50'
                    }`}
                    data-testid={`variant-${variant.id}-${option.name}`}
                  >
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value={option.name} className="sr-only" />
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                        selectedVariants[variant.id]?.name === option.name
                          ? 'border-accent bg-accent'
                          : 'border-muted-foreground/30'
                      }`}>
                        {selectedVariants[variant.id]?.name === option.name && (
                          <Check className="w-3 h-3 text-accent-foreground" />
                        )}
                      </div>
                      <span className="text-sm">{option.name}</span>
                    </div>
                    {option.price > 0 && (
                      <span className="text-sm text-accent">+${option.price}</span>
                    )}
                  </label>
                ))}
              </RadioGroup>
            </div>
          ))}

          {/* Extras */}
          {product.extras && product.extras.length > 0 && (
            <div>
              <Label className="text-sm font-medium">Extras</Label>
              <div className="mt-2 space-y-2">
                {product.extras.map(extra => (
                  <label
                    key={extra.id}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedExtras.find(e => e.id === extra.id)
                        ? 'border-accent bg-accent/5'
                        : 'border-border hover:border-accent/50'
                    }`}
                    data-testid={`extra-${extra.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={!!selectedExtras.find(e => e.id === extra.id)}
                        onCheckedChange={() => handleExtraToggle(extra)}
                        className="sr-only"
                      />
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        selectedExtras.find(e => e.id === extra.id)
                          ? 'border-accent bg-accent'
                          : 'border-muted-foreground/30'
                      }`}>
                        {selectedExtras.find(e => e.id === extra.id) && (
                          <Check className="w-3 h-3 text-accent-foreground" />
                        )}
                      </div>
                      <span className="text-sm">{extra.name}</span>
                    </div>
                    <span className="text-sm text-accent">+${extra.price}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Note */}
          <div>
            <Label className="text-sm font-medium">Notas especiales</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ej: Sin cebolla, extra salsa, alergia a..."
              className="mt-2 min-h-[80px] resize-none"
              data-testid="product-note"
            />
          </div>
        </div>

        {/* Footer - Add to cart */}
        <div className="sticky bottom-0 p-6 bg-background border-t border-border/50">
          <div className="flex items-center gap-4">
            {/* Quantity */}
            <div className="flex items-center gap-3 bg-secondary rounded-full px-2">
              <button
                onClick={() => setQuantity(q => Math.max(1, q - 1))}
                className="w-10 h-10 flex items-center justify-center hover:text-accent transition-colors"
                data-testid="decrease-quantity"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="text-lg font-medium w-6 text-center">{quantity}</span>
              <button
                onClick={() => setQuantity(q => q + 1)}
                className="w-10 h-10 flex items-center justify-center hover:text-accent transition-colors"
                data-testid="increase-quantity"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>

            {/* Add button */}
            <Button
              onClick={handleAddToCart}
              className="flex-1 h-12 rounded-full font-medium text-base"
              data-testid="add-to-cart-detail"
            >
              Agregar · ${totalPrice.toFixed(0)}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
